'use client';

import { c as _c } from "react/compiler-runtime";
import type { DirectPurchaseBinding, FundingQuoteReview } from '@morfi/onramp-sdk';
import { directPurchaseQuantity, directPurchaseSlippage, type FundingFeeEstimate, type FundingLimit, formatFiatUnits, fundingAllowanceUsedPercent, fundingFeeEstimateForAmount, gatewayDisclosure, isValidFundingFeeEstimate, selectFundingAllowance } from '@morfi/onramp-sdk';
import { useLocale, useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useState } from 'react';
import { AnimatedProgressCircle } from '@/components/ui/animated-progress-circle';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/app-accordion';
import { Skeleton } from '@/components/ui/app-skeleton';
import { logger } from '@/lib/telemetry/logger';
import { FundingAllowance } from '../funding-allowance';
export function FundingFees(t0) {
  const $ = _c(49);
  const {
    currency,
    decimals,
    principalUnits,
    estimate,
    purchase,
    purchaseReview,
    limits: t1,
    loading: t2,
    direction,
    walletDebitUnits
  } = t0;
  const limits = t1 === undefined ? [] : t1;
  const loading = t2 === undefined ? false : t2;
  const t = useTranslations("onramp");
  const locale = useLocale();
  const [open, setOpen] = useState("");
  let t3;
  if ($[0] !== estimate) {
    t3 = estimate !== null && !isValidFundingFeeEstimate(estimate);
    $[0] = estimate;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const invalid = t3;
  let t4;
  let t5;
  if ($[2] !== invalid) {
    t4 = () => {
      if (invalid) {
        logger.warn("onramp.fee_estimate_invalid", {
          code: "invalid_fee_sum"
        });
      }
    };
    t5 = [invalid];
    $[2] = invalid;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  const current = fundingFeeEstimateForAmount(estimate, currency, decimals, principalUnits);
  const summaryLimit = selectFundingAllowance(limits);
  let t6;
  if ($[5] !== t) {
    t6 = t("limit_daily_used");
    $[5] = t;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t) {
    t7 = t("limit_monthly_used");
    $[7] = t;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t) {
    t8 = t("limit_yearly_used");
    $[9] = t;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t6 || $[12] !== t7 || $[13] !== t8) {
    t9 = {
      daily: t6,
      monthly: t7,
      yearly: t8
    };
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const limitLabels = t9;
  let t10;
  if ($[15] !== currency || $[16] !== decimals || $[17] !== locale) {
    t10 = function money(value) {
      return formatFiatUnits(value, decimals, currency, locale);
    };
    $[15] = currency;
    $[16] = decimals;
    $[17] = locale;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const money = t10;
  let t11;
  if ($[19] !== locale || $[20] !== purchase || $[21] !== purchaseReview || $[22] !== t) {
    t11 = purchase && purchaseReview && <div className="flex flex-col gap-2 text-caption"><p>{t("you_receive")}:{" "}{directPurchaseQuantity(purchase, purchaseReview.receivedUnits, locale)}</p><p>{t("direct_purchase_slippage")}: {directPurchaseSlippage(purchaseReview, locale)} ·{" "}{gatewayDisclosure.slippageExplanation}</p><p>{gatewayDisclosure.beforeLink}<a href={gatewayDisclosure.link} target="_blank" rel="noopener noreferrer" className="underline">{gatewayDisclosure.linkLabel}</a>{gatewayDisclosure.afterLink}</p></div>;
    $[19] = locale;
    $[20] = purchase;
    $[21] = purchaseReview;
    $[22] = t;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const t12 = summaryLimit ? <AnimatedProgressCircle label={limitLabels[summaryLimit.period]} progress={fundingAllowanceUsedPercent(summaryLimit)} diameter={20} strokeWidth={2} strokeClassName="stroke-foreground-brand" trackClassName="stroke-foreground-brand" /> : <span aria-hidden="true" className="size-5 shrink-0 rounded-full border-2 border-border-brand/20" />;
  let t13;
  if ($[24] !== t) {
    t13 = t("fees_and_limits");
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== t13) {
    t14 = <span className="inline-flex items-center gap-2">{t12}{t13}</span>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t) {
    t15 = t("estimated_fees");
    $[29] = t;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t15) {
    t16 = <dt>{t15}</dt>;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== direction || $[34] !== t) {
    t17 = direction === "withdraw" ? t("wallet_debit") : t("total_charged");
    $[33] = direction;
    $[34] = t;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== t17) {
    t18 = <dt>{t17}</dt>;
    $[36] = t17;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const t19 = (purchase ? purchaseReview?.estimatedOutput : current?.estimatedOutput) ? t("estimated_receive") : t("you_receive");
  let t20;
  if ($[38] !== t19) {
    t20 = <dt>{t19}</dt>;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const t21 = "flex items-center justify-end";
  const t22 = purchase ? current && purchaseReview ? directPurchaseQuantity(purchase, purchaseReview.receivedUnits, locale) : t("fees_pending") : current ? money(current.receivedLocalUnits ?? current.principalUnits) : loading ? <Skeleton className="h-4 w-16" /> : t("fees_pending");
  let t23;
  if ($[40] !== t22) {
    t23 = <dd className={t21}>{t22}</dd>;
    $[40] = t22;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== currency || $[43] !== current || $[44] !== decimals || $[45] !== loading || $[46] !== summaryLimit || $[47] !== t) {
    t24 = summaryLimit ? <FundingAllowance limit={summaryLimit} currency={currency} decimals={decimals} /> : current ? <p className="text-caption text-foreground-secondary">{t("limits_not_provided")}</p> : loading ? <div aria-hidden="true" className="flex items-center gap-3 border-t border-border-secondary pt-3"><span className="size-8 shrink-0 rounded-full border-2 border-border-brand/20" /><div className="flex flex-1 flex-col gap-1"><Skeleton className="h-4 w-40" /><Skeleton className="h-4 w-full" /></div></div> : <p className="text-caption text-foreground-secondary">{t("allowance_pending")}</p>;
    $[42] = currency;
    $[43] = current;
    $[44] = decimals;
    $[45] = loading;
    $[46] = summaryLimit;
    $[47] = t;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  return <>{t11}<Accordion type="single" collapsible={true} value={open} onValueChange={next => {
      if (!next || !loading && current) {
        setOpen(next);
      }
    }} className="text-caption [&_[data-slot=accordion-content]]:motion-reduce:animate-none" aria-busy={loading}><AccordionItem value="fees" className="border-0"><AccordionTrigger disabled={(!current || loading) && !open} className="items-center gap-2 py-2.5 text-caption font-normal text-foreground-secondary hover:no-underline disabled:opacity-100"><span className="inline-flex w-full items-center justify-between gap-3">{t14}<span className="flex min-w-16 items-center justify-end gap-2">{current ? money(current.feeUnits) : loading ? <Skeleton className="h-4 w-16" /> : t("fees_pending")}</span></span></AccordionTrigger><AccordionContent className="pb-0"><div className="flex flex-col gap-2 rounded-control bg-background-tertiary/40 p-3"><dl className="grid grid-cols-2 gap-2">{t16}<dd className="flex items-center justify-end">{current ? money(current.feeUnits) : loading ? <Skeleton className="h-4 w-16" /> : t("fees_pending")}</dd>{t18}<dd className="flex items-center justify-end">{current ? money(direction === "withdraw" && walletDebitUnits ? walletDebitUnits : current.totalUnits) : loading ? <Skeleton className="h-4 w-16" /> : t("fees_pending")}</dd>{t20}{t23}</dl>{t24}</div></AccordionContent></AccordionItem></Accordion></>;
}